import { Suspense, lazy, useState } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@astryxdesign/core/Button';
import { Icon } from '@astryxdesign/core/Icon';
import type { CompareMode, CompareRange, DateRangeSpec } from '@qronstat/shared';
import { useApp } from '../../app/context';
import { RowsSkeleton } from '../../widgets/skeletons';
import { AdaptivePanel } from './Adaptive';
import { periodLabel } from './model';

const PeriodPickerContent = lazy(() =>
  import('./PeriodPickerContent').then((m) => ({ default: m.PeriodPickerContent })),
);

export interface PeriodValue {
  period: DateRangeSpec;
  compare: CompareMode;
  compareRange?: CompareRange | null;
  timeMode: 'msk' | 'local';
}

export function PeriodPicker({
  value,
  onChange,
  showCompare = true,
  showTimeMode = true,
}: {
  value: PeriodValue;
  onChange: (next: Partial<PeriodValue>) => void;
  showCompare?: boolean;
  showTimeMode?: boolean;
}) {
  const intl = useIntl();
  const { locale } = useApp();
  const [open, setOpen] = useState(false);
  const label = periodLabel(value.period, intl, locale);
  return (
    <AdaptivePanel
      isOpen={open}
      onOpenChange={setOpen}
      label={intl.formatMessage({ id: 'filters.period' })}
      width={380}
      trigger={(toggle) => (
        <Button
          label={label}
          size="sm"
          variant="secondary"
          icon={<Icon icon="calendar" />}
          {...(toggle ? { onClick: toggle } : {})}
        />
      )}
    >
      {open ? (
        <Suspense fallback={<RowsSkeleton rows={4} />}>
          <PeriodPickerContent
            value={value}
            onChange={onChange}
            showCompare={showCompare}
            showTimeMode={showTimeMode}
            setOpen={setOpen}
          />
        </Suspense>
      ) : null}
    </AdaptivePanel>
  );
}
